import {
  applyHistorySnapshot,
  redoHistory,
  undoHistory
} from './design-history';
import {
  applyAddToSelection,
  applyClearSelection,
  applyRemoveFromSelection,
  syncSelectedPanelFrom2D,
  syncSelectedPanelFrom3D,
  syncSelectedRoofFrom2D,
  syncSelectedRoofFrom3D
} from '../selection/selection-transitions';
import {
  applySetbacks,
  patchFireSetbacks,
  patchSegmentSetbacks
} from '../setbacks/setback-transitions';
import {
  appendGeometry,
  patchPanelsLayout,
  patchSegmentField,
  removeGeometry
} from '../geometry/geometry-transitions';
import {
  patchPlacementPrefs,
  toggleSelectedRoofPlacement,
  transitionAccordion
} from '../placement/placement-transitions';
import {
  applyProductionReset,
  applySolarProductionResult
} from '../production/production-result';

export const DesignerActionTypes = {
  PATCH: 'PATCH',
  RESET_PRODUCTION: 'RESET_PRODUCTION',
  TRANSITION_ACCORDION: 'TRANSITION_ACCORDION',
  SET_MODE: 'SET_MODE',
  SET_TOOL: 'SET_TOOL',
  SET_MOVE_MODE: 'SET_MOVE_MODE',
  SET_SELECTED_LABEL: 'SET_SELECTED_LABEL',
  SELECT_ROOF: 'SELECT_ROOF',
  SELECTION_ADD: 'SELECTION_ADD',
  SELECTION_REMOVE: 'SELECTION_REMOVE',
  SELECTION_CLEAR: 'SELECTION_CLEAR',
  SELECTION_SYNC_ROOF_FROM_3D: 'SELECTION_SYNC_ROOF_FROM_3D',
  SELECTION_SYNC_ROOF_FROM_2D: 'SELECTION_SYNC_ROOF_FROM_2D',
  SELECTION_SYNC_PANEL_FROM_3D: 'SELECTION_SYNC_PANEL_FROM_3D',
  SELECTION_SYNC_PANEL_FROM_2D: 'SELECTION_SYNC_PANEL_FROM_2D',
  APPLY_SETBACKS: 'APPLY_SETBACKS',
  PATCH_FIRE_SETBACKS: 'PATCH_FIRE_SETBACKS',
  PATCH_SEGMENT_SETBACKS: 'PATCH_SEGMENT_SETBACKS',
  GEOMETRY_APPEND_SEGMENT: 'GEOMETRY_APPEND_SEGMENT',
  GEOMETRY_APPEND_OBSTACLE: 'GEOMETRY_APPEND_OBSTACLE',
  GEOMETRY_APPEND_TREE: 'GEOMETRY_APPEND_TREE',
  GEOMETRY_APPEND_ANNOTATION: 'GEOMETRY_APPEND_ANNOTATION',
  GEOMETRY_REMOVE_SEGMENT: 'GEOMETRY_REMOVE_SEGMENT',
  GEOMETRY_REMOVE_OBSTACLE: 'GEOMETRY_REMOVE_OBSTACLE',
  GEOMETRY_REMOVE_TREE: 'GEOMETRY_REMOVE_TREE',
  GEOMETRY_REMOVE_ANNOTATION: 'GEOMETRY_REMOVE_ANNOTATION',
  PATCH_SEGMENT: 'PATCH_SEGMENT',
  PATCH_PANEL_LAYOUT: 'PATCH_PANEL_LAYOUT',
  PATCH_PLACEMENT_PREFS: 'PATCH_PLACEMENT_PREFS',
  TOGGLE_SELECTED_ROOF_PLACEMENT: 'TOGGLE_SELECTED_ROOF_PLACEMENT',
  APPLY_PANEL_HARDWARE: 'APPLY_PANEL_HARDWARE',
  APPLY_INVERTER_CONFIG: 'APPLY_INVERTER_CONFIG',
  APPLY_SHADING_STATUS: 'APPLY_SHADING_STATUS',
  MARK_SHADING_STALE: 'MARK_SHADING_STALE',
  APPLY_PRODUCTION_RESULT: 'APPLY_PRODUCTION_RESULT',
  HISTORY_UNDO: 'HISTORY_UNDO',
  HISTORY_REDO: 'HISTORY_REDO'
};

function stripInternalFlags(state) {
  if (!state || typeof state !== 'object') return state;
  if (!('__setbacksChanged' in state)) return state;
  const { __setbacksChanged, ...rest } = state;
  return rest;
}

export function applyPanelHardware(state, hardware = {}) {
  const currentPackage = state.package || {};
  return {
    ...state,
    package: {
      ...currentPackage,
      ...hardware,
      panel:
        hardware.panel !== undefined ? hardware.panel : currentPackage.panel,
      selectedPanelSpec:
        hardware.selectedPanelSpec !== undefined
          ? hardware.selectedPanelSpec
          : currentPackage.selectedPanelSpec,
      pendingPanel:
        hardware.pendingPanel !== undefined
          ? hardware.pendingPanel
          : currentPackage.pendingPanel
    }
  };
}

export function applyInverterConfig(state, config = {}) {
  const currentPackage = state.package || {};
  const next = {
    ...state,
    package: {
      ...currentPackage,
      inverter:
        config.inverter !== undefined
          ? config.inverter
          : currentPackage.inverter,
      inverterQuantity:
        config.inverterQuantity !== undefined
          ? config.inverterQuantity
          : currentPackage.inverterQuantity,
      additional_inverters:
        config.additional_inverters !== undefined
          ? config.additional_inverters
          : config.additionalInverters !== undefined
            ? config.additionalInverters
            : currentPackage.additional_inverters,
      selectedInverterSpec:
        config.selectedInverterSpec !== undefined
          ? config.selectedInverterSpec
          : currentPackage.selectedInverterSpec
    }
  };

  if (typeof config.primaryInverterAutoQuantity === 'boolean') {
    next.primaryInverterAutoQuantity = config.primaryInverterAutoQuantity;
    next.hasAutoCalculatedInverterQuantity =
      !config.primaryInverterAutoQuantity;
  }
  if (typeof config.hasAutoCalculatedInverterQuantity === 'boolean') {
    next.hasAutoCalculatedInverterQuantity =
      config.hasAutoCalculatedInverterQuantity;
  }

  return next;
}

export function applyShadingStatus(state, status) {
  const updates = { shadingStatus: status };
  if (status === 0) {
    updates.shadingHasRun = true;
    updates.shadingNeedsRerun = false;
    updates.shadingRerunNoticeShown = false;
  }
  return { ...state, ...updates };
}

export function markShadingStale(state, { clearPanels = false } = {}) {
  const next = {
    ...state,
    shadingNeedsRerun: Boolean(state.shadingHasRun),
    shadingRerunNoticeShown: true,
    productionHandled: false
  };
  if (clearPanels) {
    next.panels = [];
  }
  return next;
}

export function designerReducer(state, action) {
  if (!action || typeof action !== 'object') return state;
  const { type } = action;

  switch (type) {
    case DesignerActionTypes.PATCH:
      return { ...state, ...(action.patch || {}) };

    case DesignerActionTypes.RESET_PRODUCTION:
      return applyProductionReset(state);

    case DesignerActionTypes.TRANSITION_ACCORDION:
      return transitionAccordion(state, action.accordion, {
        currentTab: action.currentTab
      });

    case DesignerActionTypes.SET_MODE:
      return { ...state, mode: action.mode };

    case DesignerActionTypes.SET_TOOL:
      return { ...state, tool: action.tool };

    case DesignerActionTypes.SET_MOVE_MODE:
      return { ...state, moveMode: action.moveMode };

    case DesignerActionTypes.SET_SELECTED_LABEL:
      return {
        ...state,
        selectedLabel: action.selectedLabel,
        mode: action.mode !== undefined ? action.mode : 'label'
      };

    case DesignerActionTypes.SELECT_ROOF: {
      const selectedRoof =
        action.index === undefined || action.index === null ? -1 : action.index;
      const withRoof = { ...state, selectedRoof };
      const roofSync = syncSelectedRoofFrom2D(withRoof);
      return roofSync ? { ...withRoof, ...roofSync } : withRoof;
    }

    case DesignerActionTypes.SELECTION_ADD:
      return applyAddToSelection(state, action.index, action.objectType, {
        removeOld: action.removeOld,
        currentAccordion: action.currentAccordion
      });

    case DesignerActionTypes.SELECTION_REMOVE:
      return applyRemoveFromSelection(state, action.index, action.objectType);

    case DesignerActionTypes.SELECTION_CLEAR:
      return applyClearSelection(
        state,
        action.objectType,
        Boolean(action.forceClear)
      );

    case DesignerActionTypes.SELECTION_SYNC_ROOF_FROM_3D: {
      const patch = syncSelectedRoofFrom3D(state);
      return patch ? { ...state, ...patch } : state;
    }

    case DesignerActionTypes.SELECTION_SYNC_ROOF_FROM_2D: {
      const patch = syncSelectedRoofFrom2D(state);
      return patch ? { ...state, ...patch } : state;
    }

    case DesignerActionTypes.SELECTION_SYNC_PANEL_FROM_3D:
      return { ...state, ...syncSelectedPanelFrom3D(state) };

    case DesignerActionTypes.SELECTION_SYNC_PANEL_FROM_2D:
      return { ...state, ...syncSelectedPanelFrom2D(state) };

    case DesignerActionTypes.APPLY_SETBACKS:
      return stripInternalFlags(
        applySetbacks(state, {
          fireSetbacks: action.fireSetbacks,
          setbackMode: action.setbackMode,
          forceUpdate: action.forceUpdate
        })
      );

    case DesignerActionTypes.PATCH_FIRE_SETBACKS:
      return stripInternalFlags(
        patchFireSetbacks(state, action.setback, action.label)
      );

    case DesignerActionTypes.PATCH_SEGMENT_SETBACKS:
      return patchSegmentSetbacks(state, action.editLines, action.setback);

    case DesignerActionTypes.GEOMETRY_APPEND_SEGMENT:
      return appendGeometry(state, 'segment', action.segment);

    case DesignerActionTypes.GEOMETRY_APPEND_OBSTACLE:
      return appendGeometry(state, 'obstacle', action.obstacle);

    case DesignerActionTypes.GEOMETRY_APPEND_TREE:
      return appendGeometry(state, 'tree', action.tree);

    case DesignerActionTypes.GEOMETRY_APPEND_ANNOTATION:
      return appendGeometry(state, 'annotation', action.annotation);

    case DesignerActionTypes.GEOMETRY_REMOVE_SEGMENT:
      return removeGeometry(state, 'segment', action.id);

    case DesignerActionTypes.GEOMETRY_REMOVE_OBSTACLE:
      return removeGeometry(state, 'obstacle', action.id);

    case DesignerActionTypes.GEOMETRY_REMOVE_TREE:
      return removeGeometry(state, 'tree', action.id);

    case DesignerActionTypes.GEOMETRY_REMOVE_ANNOTATION:
      return removeGeometry(state, 'annotation', action.id);

    case DesignerActionTypes.PATCH_SEGMENT:
      return {
        ...state,
        segments: patchSegmentField(
          state.segments || [],
          action.index,
          action.patch || {}
        )
      };

    case DesignerActionTypes.PATCH_PANEL_LAYOUT:
      return patchPanelsLayout(state, action.panels);

    case DesignerActionTypes.PATCH_PLACEMENT_PREFS:
      return patchPlacementPrefs(state, action.prefs || action.patch || {});

    case DesignerActionTypes.TOGGLE_SELECTED_ROOF_PLACEMENT:
      return toggleSelectedRoofPlacement(state, action.onlySelectedRoof);

    case DesignerActionTypes.APPLY_PANEL_HARDWARE:
      return applyPanelHardware(state, action.hardware || action.patch || {});

    case DesignerActionTypes.APPLY_INVERTER_CONFIG:
      return applyInverterConfig(state, action.config || action.patch || {});

    case DesignerActionTypes.APPLY_SHADING_STATUS:
      return applyShadingStatus(state, action.status);

    case DesignerActionTypes.MARK_SHADING_STALE:
      return markShadingStale(state, {
        clearPanels: action.clearPanels
      });

    case DesignerActionTypes.APPLY_PRODUCTION_RESULT:
      return applySolarProductionResult(
        state,
        action.result || {},
        action.ctx || {}
      );

    case DesignerActionTypes.HISTORY_UNDO:
    case DesignerActionTypes.HISTORY_REDO: {
      const historyResult =
        type === DesignerActionTypes.HISTORY_UNDO
          ? undoHistory(action.histories || [], action.historyState || {})
          : redoHistory(action.histories || [], action.historyState || {});
      return applyHistorySnapshot(state, historyResult.snapshot);
    }

    default:
      return state;
  }
}

export function dispatchDesignerAction(state, action) {
  if (
    action?.type === DesignerActionTypes.HISTORY_UNDO ||
    action?.type === DesignerActionTypes.HISTORY_REDO
  ) {
    const historyResult =
      action.type === DesignerActionTypes.HISTORY_UNDO
        ? undoHistory(action.histories || [], action.historyState || {})
        : redoHistory(action.histories || [], action.historyState || {});
    return {
      state: applyHistorySnapshot(state, historyResult.snapshot),
      histories: historyResult.histories,
      historyState: historyResult.historyState,
      snapshot: historyResult.snapshot
    };
  }

  return { state: designerReducer(state, action) };
}
